<template>
    <ModalComponent
        :is-open="isModalOpen"
        :show-footer-buttons="true"
        :title="singlePipeline ? `Update Tags on Pipeline` : `Bulk Update Tags on ${selectedPipelineCount} Pipeline(s)`"
        size="lg"
        position="center"
        @update:isOpen="handleClose">
        
        <div class="space-y-6">
            <!-- Tags Multiselect -->
            <div class="space-y-2">
                <Label for="tag-select">Select Tags</Label>
                <MultiSelect
                    id="tag-select"
                    v-model="selectedTagIds"
                    :options="tagOptions"
                    placeholder="Select tags to apply"
                    :option-label="'name'"
                    :track-by="'id'"
                    :aria-invalid="selectedTags.length === 0"
                />
            </div>

            <!-- Environment tag required alert -->
            <Alert v-if="!selectedTags?.find((t) => t.type?.includes(TAG_TYPES.ENVIRONMENT))">
                <Info class="h-4 w-4" />
                <AlertTitle>Environment Tag Required</AlertTitle>
                <AlertDescription>
                    You must include an environment tag, either Production or Development.
                </AlertDescription>
            </Alert>

            <!-- Warning about overwriting tags -->
            <Alert v-if="selectedTags.length" variant="default">
                <AlertTriangle class="h-4 w-4" />
                <AlertTitle>Tags Will Be Replaced</AlertTitle>
                <AlertDescription>
                    <span v-if="!singlePipeline">
                        Saving will completely overwrite all existing tags on the selected pipelines with the selected tags.
                    </span>
                    <span v-else>
                        Saving will completely overwrite all existing tags on this pipeline with the selected tags.
                    </span>
                </AlertDescription>
            </Alert>

            <!-- Update All Pipelines Checkbox -->
            <div v-if="!singlePipeline && allSelected && selectedPipelineCount < tagsStore.total" class="flex items-center space-x-2">
                <Checkbox v-model="updateAllPipelines" id="updateAllPipelines" />
                <Label for="updateAllPipelines">I want to update all pipelines in the database</Label>
            </div>
        </div>

        <template #footer>
            <Button @click="handleCancel" variant="secondary">
                Cancel
            </Button>
            <LoadingButton
                :loading="saveIsLoading"
                :disabled="isSaveDisabled"
                @click="handleSave">
                Save
            </LoadingButton>
        </template>
    </ModalComponent>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import ModalComponent from '@/components/global/modals/Modal.vue';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { MultiSelect } from '@/components/ui/multi-select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { LoadingButton } from '@/components/ui/loading-button';
import { AlertTriangle, Info } from 'lucide-vue-next';
import { useLoaderStore } from '@/stores/loader.store';
import { useTagsStore } from '@/stores/tags.store';
import { MAX_ONE_TAG_PER_TYPE, TAG_TYPES } from '@/utils/constants';
import { toast } from '@/composables/useToast';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    },
    selectedPipelineCount: {
        type: Number,
        default: 0
    },
    allSelected: {
        type: Boolean,
        default: false
    },
    singlePipeline: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:isOpen', 'save']);
const isModalOpen = ref(props.isOpen);
const selectedTags = ref([]);
const updateAllPipelines = ref(false);

// Watch for prop changes
watch(() => props.isOpen, (newValue) => {
    isModalOpen.value = newValue;
});
const loaderStore = useLoaderStore();
const tagsStore = useTagsStore();

const saveIsLoading = computed(() => loaderStore.isLoading('pipelines.bulkUpdateTags'));
const tagOptions = computed(() => tagsStore.tagsPerTypeForEntityCRUD[TAG_TYPES.PIPELINE]);
const isSaveDisabled = computed(() => selectedTags.value.length === 0);

const selectedTagIds = computed({
    get() {
        return selectedTags.value.map(tag => tag.id);
    },
    set(ids) {
        selectedTags.value = ids.map(id =>
            tagOptions.value.find(tag => tag.id === id)
        ).filter(Boolean);
    }
});

const handleClose = (value) => {
    isModalOpen.value = value;
    emit('update:isOpen', value);
};

const handleCancel = () => {
    emit('update:isOpen', false);
};

const handleSave = async () => {
    try {
        if (!selectedTags.value?.length) {
            toast.error('Please select at least one tag. Having at least an environment-related tag (Production/Development) is mandatory.');

            return;
        }

        if (!selectedTags.value?.find((tag) => tag.type?.includes(TAG_TYPES.ENVIRONMENT))) {
            toast.error('Please select at least one environment-related tag (Production/Development).');

            return;
        }

        emit('save', { updateAll: updateAllPipelines.value, tags: selectedTags.value?.map((t) => t.id) });
    } catch (error) {
        console.error('Error updating pipelines:', error);
    }
};

const handleTagSelection = () => {
    const existingExclusiveTag = selectedTags.value.filter((tag) => tag.type?.length && MAX_ONE_TAG_PER_TYPE.some((type) => tag.type.includes(type)));

    if (existingExclusiveTag?.length > 1) {
        toast.warning(`Only one tag of the same ${MAX_ONE_TAG_PER_TYPE.join(', ')} type can be selected`);

        selectedTags.value = selectedTags.value.filter((tag) => tag.type?.length && !MAX_ONE_TAG_PER_TYPE.some((type) => tag.type.includes(type)));

        selectedTags.value.push(existingExclusiveTag[0]);
        return;
    }
};

// Watch for changes in selectedTags to handle tag selection validation
watch(selectedTags, () => {
    handleTagSelection();
}, { deep: true });
</script>

<style scoped>
/* Add any necessary styling here */
</style>
